<template>
  <b-modal :title="helper.trans('labels.transfer')"
           :modelValue="modal !== null"
           footer-class=""
           @update:modelValue="$event === false && (modal = null)">
    <form v-if="account">
      <div class="row mb-2">
        <div class="col-sm-12">

          <div class="row mt-2">
            <div class="col-sm-12 d-flex align-items-center p-2 mt-3 justify-content-center">
              <AccountWidget show-icon :account="account"></AccountWidget>
            </div>
          </div>

          <div class="row mt-2">
            <label for="amount" class="col-sm-12 col-form-label text-capitalize-first">{{ helper.trans("labels.amount") }}
              <a href="#" @click.prevent="form.amount = numberFormatter.formatNumber(account.sum.amount, account.sum.subunit)">
                <small>{{ numberFormatter.formatNumber(account.sum.amount, account.sum.subunit) }}</small>
              </a>
            </label>
            <div class="col-sm-12">
              <FormInputWrapper v-slot="parentProps" :errors="getErrors(`amount`)" >
                <input v-model="form.amount"
                       :class="parentProps.class"
                       type="text" class="form-control " id="amount">
              </FormInputWrapper>
            </div>
          </div>

          <div class="row mt-2">
            <label for="traders" class="col-sm-12 col-form-label text-capitalize-first">{{ helper.trans("labels.store") }}</label>
            <div class="col-sm-12">
              <FormInputWrapper v-slot="parentProps" :errors="getErrors(`targetAccountId`)">
                <Multiselect id="traders"
                             v-model="form.store"
                             :options="options"
                             :custom-label="(opt) => `${opt.merchantName} - ${opt.name} (${opt.currency})`"
                             track-by="id"
                             select-label="Select"
                             :placeholder="helper.trans('labels.stores')"
                             open-direction="bottom"
                             :multiple="multiple"
                             :searchable="true"
                             :loading="isLoading"
                             :internal-search="false"
                             :clear-on-select="!multiple"
                             :close-on-select="!multiple"
                             :options-limit="100"
                             :limit="1000"
                             :show-no-results="false"
                             :hide-selected="multiple"
                             @search-change="asyncFindStores"
                >
                </Multiselect>
              </FormInputWrapper>
            </div>
          </div>
        </div>
      </div>
    </form>
    <template #footer>
      <div class="d-flex justify-content-between w-100">
        <button type="button" @click="modal = null" class="btn btn-outline-secondary btn-sm text-capitalize-first">
          {{ helper.trans('button.cancel') }}
        </button>
        <LoaderWrapper ref="saveLoader" :zoom="0.5">
          <button type="button" @click="save()" class="btn btn-outline-primary btn-sm text-capitalize-first">
            {{ helper.trans('button.ok') }}
          </button>
        </LoaderWrapper>
      </div>
    </template>
  </b-modal>
</template>

<script lang="ts">
import {defineComponent, onMounted, Prop, PropType, reactive, toRefs, watch} from "vue";
import {useStore} from "vuex";
import {helper} from "@/service/CommonHelper";
import AccountDTO from "@/models/AccountDTO";
import {numberFormatter} from "@/service/NumberFormatter";
import ValidationErrorDTO from "@/models/ValidationErrorDTO";
import APIClient from "@/service/APIClient";
import {useNotification} from "@kyvg/vue3-notification";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import Multiselect from "vue-multiselect";
import TraderAccountDTO from "@/models/TraderAccountDTO";
import AccountWidget from "@/components/AccountWidget.vue";
import MerchantStoreDTO from "@/models/MerchantStoreDTO";
import MerchantTransferFormDTO from "@/models/form/MerchantTransferFormDTO";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import AccountTransferRequestDTO from "@/models/request/AccountTransferRequestDTO";

const api = new APIClient();
const notification = useNotification()

export default defineComponent({
  props: {
    multiple: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    modelValue: {
      type: Array as PropType<any>,
      default: null
    } as Prop<any>,
    store: {
      type: Object as PropType<MerchantStoreDTO>,
      default: null
    } as Prop<MerchantStoreDTO>,
    account: {
      type: Object as PropType<AccountDTO>,
      default: null
    } as Prop<AccountDTO>
  },
  emits: ['updated'],
  components: {
    LoaderWrapper,
    AccountWidget,
    FormInputWrapper,
    Multiselect
  },

  setup(props, {emit}) {
    const store = useStore();

    const state = reactive({
      modal: null as boolean|null,
      account: null as AccountDTO|null,
      errors: {} as ValidationErrorDTO[]|any,
      form: {} as MerchantTransferFormDTO,
      isLoading: false,
      options: [] as MerchantStoreDTO[],
    });

    watch(
        () => props.store,
        async (newStore) => {
          if (newStore) {
            state.form = {
              amount: null,
              store: null,
              account: null,
            };
            state.errors = [];
            await asyncFindStores('');
          }
        },
        { immediate: true }
    );

    const show = (account: AccountDTO) => {
      state.modal = true
      state.account = account
      state.form = {
        amount: null,
        store: null,
        account: null,
      };
      state.errors = {}

      asyncFindStores()
    }

    const setErrors = (data: ValidationErrorDTO[]) => {
      state.errors = {};
      for (const error of data) {
        if (!error.field) {
          notification.notify({type: 'error', title: error.message});
          continue;
        }
        if (!state.errors.hasOwnProperty(error.field)) {
          state.errors[error.field] = [];
        }
        state.errors[error.field].push(error)
      }
    }

    const getErrors = (path: string) => {
      if (!state.errors.hasOwnProperty(path)) {
        return [];
      }

      return state.errors[path]
    }

    const getForm = (): MerchantTransferFormDTO => {
      return state.form
    }

    const asyncFindStores = async (query = '') => {
      state.isLoading = true;
      try {
        const response = await api.merchantStores(
            {limit: 100, offset: 0},
            {query}
        );

        let stores = [];
        for (const store of response.items) {
          for (const msAccount of store.accounts) {
            if (msAccount.account.currency !== state.account?.currency) {
              continue;
            }
            if (msAccount.id === state.account?.id) {
              continue;
            }

            stores.push(store)
          }
        }

        state.options = stores;
      } catch (error) {
        console.error('Error fetching stores:', error);
        state.errors = (error as unknown as any).response?.data?.errors || {};
        state.options = [];
      } finally {
        state.isLoading = false;
      }
    };

    const setForm = (form: MerchantTransferFormDTO) => {
      state.form = {
        ...form,
      }
    }

    const accountLabel = (opt: TraderAccountDTO) => {
      return `${opt.traderEmail} - ${numberFormatter.formatNumber(opt.account.sum, opt.account.sum.subunit)} ${opt.account.sum.currency}`
    }

    const save = (): Promise<any> => {
      setErrors([]);
      const form = getForm();
      form.account = state.account;

      const targetAccountId = form.store!.accounts.find((msAccount) => msAccount?.account.currency === state.account!.currency)?.id

      return api.accountTransferCreate(form.account!.id,{
        targetAccountId: targetAccountId,
        amount: form.amount ? form.amount.replaceAll(" ", '') : ''
      } as AccountTransferRequestDTO)
          .then((resp) => {
            notification.notify({title: helper.trans('transfer.notice.created')});
            emit('updated');
            state.modal = null;
          })
          .catch((e: any) => {
            if (e.response && e.request && e.request.status === 400 && e.response.data) {
              setErrors(e.response.data)
            } else {
              console.log(e);
            }
            throw e;
          })
    }

    onMounted(() => {
      asyncFindStores('')
    });

    return {
      ...toRefs(state),
      show,
      getForm,
      setForm,
      save,
      store,
      helper,
      getErrors,
      numberFormatter,
      asyncFindStores,
      accountLabel
    }
  },
})
</script>
